import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { apiGet, apiPostJson } from './client'

/**
 * Settings > Softsee > CVE database - `GET|POST /api/v2/settings/vulnerabilities/sync`
 * (`SettingsVulnerabilitiesSyncController`). Numai administratorii de platforma;
 * altcineva primeste 403. Cheia cronului ramane pe server.
 */

export type CveSyncLine = { tone: 'ok' | 'warn' | 'bad' | string; text: string; hint: string }

export type CveSyncStep = { step: string; ok: boolean; message: string }

export type CveSyncRun = {
  state: 'running' | 'done' | 'failed' | 'busy' | 'stalled' | string
  requested_by: string
  requested_at: string | null
  finished_at: string | null
  step: string
  slices: number
  message: string
  error: string
  steps: CveSyncStep[]
}

export type CveSyncStatus = {
  can_sync: boolean
  configured: boolean
  line: CveSyncLine
  state: {
    available: boolean
    running: boolean
    started_at: string | null
    last_run_at: string | null
    last_message: string
    last_modified: string | null
  }
  counts: { cves: number; kev: number | null; epss: number | null }
  run: CveSyncRun | null
  in_progress: boolean
}

export type CveSyncStarted = CveSyncStatus & { ok: boolean; message: string }

export const CVE_SYNC_KEY = ['settings', 'vulnerabilities', 'sync'] as const

/** Cat de des intreaba ecranul cat timp merge o rulare. */
export const CVE_SYNC_POLL_MS = 5000

export function cveSyncQuery() {
  return queryOptions({
    queryKey: CVE_SYNC_KEY,
    queryFn: () => apiGet<CveSyncStatus>('/settings/vulnerabilities/sync'),
    refetchInterval: (query) => (query.state.data?.in_progress ? CVE_SYNC_POLL_MS : false),
  })
}

export function startCveSync(followUp: boolean) {
  return apiPostJson<CveSyncStarted>('/settings/vulnerabilities/sync', { follow_up: followUp })
}

/**
 * Lista CVE-urilor din baza - `GET /api/v2/settings/vulnerabilities/cves?q=&page=`.
 * Cautarea (id, descriere, produs/furnizor) si paginile se fac pe server;
 * cele noi intai. `kev`/`epss` sunt `null` cand tabelele lor nu exista.
 */
export type CveListItem = {
  cve_id: string
  description: string
  published: string | null
  last_modified: string | null
  score: number | null
  severity: string
  cvss_version: string | null
  kev: boolean | null
  known_ransomware: boolean | null
  epss: number | null
  epss_percentile: number | null
}

export type CveListPage = {
  q: string
  total: number
  page: number
  per_page: number
  has_kev: boolean
  has_epss: boolean
  items: CveListItem[]
}

export const CVE_LIST_KEY = ['settings', 'vulnerabilities', 'cves'] as const

export function cveListQuery(search: string, page: number) {
  const params = new URLSearchParams({ page: String(page) })
  if (search !== '') params.set('q', search)
  return queryOptions({
    queryKey: [...CVE_LIST_KEY, search, page] as const,
    queryFn: () => apiGet<CveListPage>(`/settings/vulnerabilities/cves?${params.toString()}`),
    placeholderData: keepPreviousData,
  })
}
